<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ManagedAppointment, PublicAvailabilitySlot } from '@terapeut-ia/shared-types'
import {
  cancelAppointment,
  getAvailability,
  lookupAppointment,
  rescheduleAppointment,
} from '../services/api'

const emit = defineEmits<{ close: [] }>()

const phone = ref('')
const managementCode = ref('')
const appointment = ref<ManagedAppointment | null>(null)
const loading = ref(false)
const actionLoading = ref(false)
const error = ref<string | null>(null)
const message = ref<string | null>(null)
const rescheduleOpen = ref(false)
const cancelOpen = ref(false)
const date = ref(new Date().toLocaleDateString('en-CA'))
const slots = ref<PublicAvailabilitySlot[]>([])
const loadingSlots = ref(false)

const maxDate = computed(() => {
  const value = new Date()
  value.setDate(value.getDate() + 60)
  return value.toLocaleDateString('en-CA')
})

function formatDateTime(epoch: number, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(epoch * 1000))
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)
}

function statusLabel(status: ManagedAppointment['status']): string {
  const labels: Record<ManagedAppointment['status'], string> = {
    PENDING: 'Aguardando confirmação',
    CONFIRMED: 'Confirmado',
    IN_PROGRESS: 'Em atendimento',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
    NO_SHOW: 'Não compareceu',
  }
  return labels[status]
}

async function lookup(): Promise<void> {
  loading.value = true
  error.value = null
  message.value = null
  try {
    appointment.value = await lookupAppointment({
      phone: phone.value,
      managementCode: managementCode.value,
    })
    rescheduleOpen.value = false
    cancelOpen.value = false
  } catch (cause) {
    appointment.value = null
    error.value = cause instanceof Error ? cause.message : 'Não foi possível localizar o agendamento.'
  } finally {
    loading.value = false
  }
}

async function loadSlots(): Promise<void> {
  if (!appointment.value || !rescheduleOpen.value) return
  loadingSlots.value = true
  error.value = null
  try {
    const result = await getAvailability(appointment.value.serviceId, date.value)
    slots.value = result.slots
  } catch (cause) {
    slots.value = []
    error.value = cause instanceof Error ? cause.message : 'Não foi possível consultar os novos horários.'
  } finally {
    loadingSlots.value = false
  }
}

async function reschedule(slot: PublicAvailabilitySlot): Promise<void> {
  if (!appointment.value) return
  actionLoading.value = true
  error.value = null
  message.value = null
  try {
    appointment.value = await rescheduleAppointment({
      appointmentId: appointment.value.id,
      phone: phone.value,
      managementCode: managementCode.value,
      startAt: slot.startAt,
      idempotencyKey: crypto.randomUUID(),
    })
    rescheduleOpen.value = false
    slots.value = []
    message.value = 'Agendamento reagendado. O horário anterior foi liberado.'
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Não foi possível reagendar.'
    await loadSlots()
  } finally {
    actionLoading.value = false
  }
}

async function cancel(): Promise<void> {
  if (!appointment.value) return
  actionLoading.value = true
  error.value = null
  message.value = null
  try {
    appointment.value = await cancelAppointment({
      appointmentId: appointment.value.id,
      phone: phone.value,
      managementCode: managementCode.value,
      reason: 'Cancelado pelo cliente no site',
      idempotencyKey: crypto.randomUUID(),
    })
    cancelOpen.value = false
    rescheduleOpen.value = false
    message.value = 'Agendamento cancelado e horário liberado.'
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Não foi possível cancelar.'
  } finally {
    actionLoading.value = false
  }
}

function clearAppointment(): void {
  appointment.value = null
  message.value = null
  error.value = null
  rescheduleOpen.value = false
  cancelOpen.value = false
  slots.value = []
}

watch(date, () => {
  if (rescheduleOpen.value) void loadSlots()
})
</script>

<template>
  <div class="booking-backdrop" @click.self="emit('close')">
    <section class="booking-card">
      <div class="flex items-start justify-between gap-4">
        <div>
          <p class="section-label">Área do cliente</p>
          <h2 class="mt-2 font-display text-3xl">Gerenciar agendamento</h2>
          <p class="mt-2 text-sm text-white/45">Use o telefone informado e o código recebido na confirmação.</p>
        </div>
        <button class="booking-close" type="button" @click="emit('close')">×</button>
      </div>

      <div v-if="error" class="mt-5 rounded-2xl border border-red-400/20 bg-red-500/10 p-4 text-sm text-red-100">{{ error }}</div>
      <div v-if="message" class="mt-5 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4 text-sm text-emerald-100">{{ message }}</div>

      <form v-if="!appointment" class="mt-7 grid gap-4" @submit.prevent="lookup">
        <label>
          <span class="text-xs text-white/50">Telefone com DDD</span>
          <input v-model="phone" class="booking-field mt-2" autocomplete="tel" inputmode="tel" placeholder="(41) 99999-9999" required type="tel">
        </label>
        <label>
          <span class="text-xs text-white/50">Código do agendamento</span>
          <input v-model="managementCode" class="booking-field mt-2 uppercase" autocomplete="one-time-code" maxlength="20" placeholder="ABCD-2345" required type="text">
        </label>
        <button class="primary-button mt-2" :disabled="loading">{{ loading ? 'Consultando...' : 'Consultar agendamento' }}</button>
      </form>

      <div v-else class="mt-7">
        <div class="rounded-3xl border border-white/10 bg-white/[.025] p-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p class="section-label">{{ statusLabel(appointment.status) }}</p>
              <h3 class="mt-3 font-display text-2xl">{{ appointment.serviceName }}</h3>
              <p class="mt-2 text-sm text-white/50">{{ appointment.clientName }} · {{ appointment.phoneDisplay }}</p>
            </div>
            <strong class="text-lg">{{ formatPrice(appointment.priceCents) }}</strong>
          </div>
          <p class="mt-5 border-t border-white/10 pt-5 text-sm text-white/65">{{ formatDateTime(appointment.scheduledStartAt, appointment.timezone) }}</p>
        </div>

        <div v-if="rescheduleOpen" class="mt-5 rounded-3xl border border-white/10 bg-black/20 p-5">
          <div class="flex items-center justify-between gap-4">
            <div><p class="section-label">Reagendamento</p><h4 class="mt-2 font-display text-xl">Escolha o novo horário</h4></div>
            <button class="text-sm text-white/50" type="button" @click="rescheduleOpen = false">Fechar</button>
          </div>
          <input v-model="date" class="booking-field mt-5" type="date" :min="new Date().toLocaleDateString('en-CA')" :max="maxDate">
          <div v-if="loadingSlots" class="py-8 text-center text-sm text-white/40">Consultando agenda...</div>
          <div v-else-if="slots.length" class="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4">
            <button v-for="slot in slots" :key="slot.startAt" class="slot-button" type="button" :disabled="actionLoading" @click="reschedule(slot)">{{ slot.localTime }}</button>
          </div>
          <div v-else class="mt-5 rounded-2xl border border-dashed border-white/10 p-6 text-center text-sm text-white/40">Nenhum horário disponível nesta data.</div>
        </div>

        <div v-if="cancelOpen" class="mt-5 rounded-3xl border border-red-400/20 bg-red-500/[.07] p-5">
          <strong>Confirmar cancelamento?</strong>
          <p class="mt-2 text-sm leading-6 text-white/50">O horário será liberado imediatamente. Esta ação ficará registrada no histórico.</p>
          <div class="mt-4 flex gap-3">
            <button class="primary-button" type="button" :disabled="actionLoading" @click="cancel">{{ actionLoading ? 'Cancelando...' : 'Confirmar cancelamento' }}</button>
            <button class="secondary-button" type="button" :disabled="actionLoading" @click="cancelOpen = false">Voltar</button>
          </div>
        </div>

        <div v-if="!rescheduleOpen && !cancelOpen" class="mt-5 flex flex-wrap gap-3">
          <button v-if="appointment.canReschedule" class="primary-button" type="button" @click="rescheduleOpen = true; loadSlots()">Reagendar</button>
          <button v-if="appointment.canCancel" class="secondary-button" type="button" @click="cancelOpen = true">Cancelar</button>
          <button class="secondary-button" type="button" @click="clearAppointment">Consultar outro</button>
        </div>
      </div>
    </section>
  </div>
</template>
